import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { validateEnv, USE_CUSTOM_AGENT } from "@/env";
import { initTelemetry } from "@/telemetry";
import { installGlobalErrorListeners } from "@/telemetry/global-error-listeners";
import { ErrorBoundary } from "@/components/error-boundary";
import { QueryProvider } from "@/providers/query-provider";
import { AuthProvider } from "@/providers/auth-provider";
import { ThemeProvider } from "@/providers/theme-provider";
import { CopilotProvider } from "@/providers/copilot-provider";
import "@/i18n";
import { router } from "./router";
import "@/styles/globals.css";

// Validate required env vars early — throws with a clear message if any are missing.
validateEnv();

// Initialise OpenTelemetry tracing before React renders.
// No-ops when VITE_OTEL_DISABLED=true or when no exporter endpoint is configured.
initTelemetry();

// Install global window.error / unhandledrejection listeners (E3).
// Must run after initTelemetry() so spans are actually recorded.
installGlobalErrorListeners();

if (import.meta.env.DEV && "serviceWorker" in navigator) {
  void navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => Promise.all(registrations.map((reg) => reg.unregister())))
    .catch((error: unknown) => {
      console.warn("Failed to unregister service workers in dev:", error);
    });

  if ("caches" in window) {
    void caches
      .keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      .catch((error: unknown) => {
        console.warn("Failed to clear caches in dev:", error);
      });
  }
}

// PWA service worker registration is handled exclusively by the <UpdatePrompt>
// component in components/pwa.tsx via useRegisterSW (virtual:pwa-register/react).
// Do NOT add a second registration here — duplicate registrations cause
// competing skipWaiting/claim cycles that force reloads when the mobile PWA
// is backgrounded and foregrounded.
// @see components/pwa.tsx — UpdatePrompt

/**
 * Mount the React application to the DOM
 *
 * Provider hierarchy:
 * - StrictMode: React development checks
 * - ErrorBoundary: Catches unhandled React errors, records OTel spans (E3)
 * - ThemeProvider: Dark/light mode management
 * - QueryProvider: TanStack Query context + devtools
 * - AuthProvider: Better Auth session management
 * - CopilotProvider: CopilotKit AI assistant context
 * - RouterProvider: TanStack Router with file-based routing
 */
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <QueryProvider>
          <AuthProvider>
            {USE_CUSTOM_AGENT ? (
              <RouterProvider router={router} />
            ) : (
              <CopilotProvider>
                <RouterProvider router={router} />
              </CopilotProvider>
            )}
          </AuthProvider>
        </QueryProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </StrictMode>
);
